import React, { useState, useRef, useEffect } from 'react';
import { useMobile } from '../../hooks';

const RVGSidebar = ({ 
  show, 
  isClosing, 
  loading, 
  results, 
  clickedPoint,
  highlightedIndex,
  onClose, 
  onResultClick 
}) => {
  const isMobile = useMobile();
  const [sheetHeight, setSheetHeight] = useState(33); // Initial height in vh
  const startY = useRef(null);
  const startHeight = useRef(null);

  // Reset height when showing
  useEffect(() => {
    if (show && isMobile) {
      setSheetHeight(33);
    }
  }, [show, isMobile]);

  if (!show) return null;

  const handleClose = () => {
    onClose();
  };

  const handleTouchStart = (e) => {
    startY.current = e.touches[0].clientY;
    startHeight.current = sheetHeight;
  };

  const handleTouchMove = (e) => {
    if (!startY.current) return;
    const currentY = e.touches[0].clientY;
    const deltaY = currentY - startY.current;
    const windowHeight = window.innerHeight;
    // Dragging down (positive delta) decreases height (as top moves down)
    const deltaVh = (deltaY / windowHeight) * 100;
    
    let newHeight = startHeight.current - deltaVh;
    
    // Clamp between 15vh and 85vh
    if (newHeight < 15) newHeight = 15;
    if (newHeight > 85) newHeight = 85;
    
    setSheetHeight(newHeight);
  };

  const handleTouchEnd = () => {
    startY.current = null;
    startHeight.current = null;
  };

  // Inline styles for mobile height control
  const panelStyle = isMobile ? {
    height: `${sheetHeight}vh`,
    maxHeight: '85vh', 
    transition: startY.current ? 'none' : 'height 0.3s ease-out'
  } : {};

  // Ensure close button is visible on mobile
  const closeBtnStyle = isMobile ? {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    background: '#f0f0f0',
    borderRadius: '50%',
    position: 'absolute',
    right: '16px',
    top: '28px', // Positioned below drag handle (24px) + small gap (4px)
    zIndex: 20,
    border: '1px solid #ddd'
  } : {};

  return (
    <div 
      className={`results-panel ${isClosing ? 'closing' : ''}`}
      style={panelStyle}
    >
      {isMobile && (
        <div 
          className="sheet-handle-bar"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            width: '100%',
            height: '24px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'grab',
            touchAction: 'none',
            borderTopLeftRadius: '20px',
            borderTopRightRadius: '20px',
            background: '#fff'
          }}
        >
          <div style={{
            width: '40px',
            height: '5px',
            backgroundColor: '#ccc',
            borderRadius: '3px'
          }}></div>
        </div>
      )}

      <div className="sidebar-header" style={isMobile ? { paddingTop: '5px', paddingRight: '50px' } : {}}>
        <h3>Reverse Geocode Results</h3>
        <button
          className="sidebar-close-btn"
          onClick={handleClose}
          aria-label="Close sidebar"
          style={closeBtnStyle}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#333"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      {loading ? (
        <div className="rvg-loading">
          <div className="spinner"></div>
          <p>Fetching location data...</p>
        </div>
      ) : results.length === 0 ? (
        <p className="empty-msg">Click on the map to fetch location info.</p>
      ) : (
        <div className="results-scroll">
          {results.map((r, idx) => {
            // Check if this is the clicked point
            const isClickedPoint = clickedPoint && 
              (r.location || r.centroid_point) &&
              Math.abs((r.location?.lat || r.centroid_point?.lat) - clickedPoint.lat) < 0.0001 && 
              Math.abs((r.location?.long || r.location?.lng || r.centroid_point?.lng) - clickedPoint.lng) < 0.0001;
            
            // Check if this result is highlighted
            const isHighlighted = highlightedIndex === idx;
            
            return (
              <div 
                key={idx} 
                className={`result-card ${isHighlighted ? 'highlighted' : ''}`}
                onClick={() => onResultClick(idx, r)}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: isClickedPoint ? "8px" : "10px" }}>
                  {isClickedPoint && (
                    <div style={{ display: "inline-flex", alignItems: "center" }}>
                      <svg width="16" height="21" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 0C5.37258 0 0 5.37258 0 12C0 18.6274 12 32 12 32C12 32 24 18.6274 24 12C24 5.37258 18.6274 0 12 0Z" fill="#FF3B30"/>
                        <circle cx="12" cy="12" r="5" fill="white"/>
                      </svg>
                    </div>
                  )}
                  <h4 style={{ margin: 0 }}>{r.entity_name || r.rooftop_name || r.name}</h4>
                </div>
                <p className="address">{r.entity_address || r.display_address || r.address}</p>
                {r.distance_from_input && r.distance_from_input > 0 ? (
                  <p className="distance">📏 {r.distance_from_input.toLocaleString()}m away</p>
                ) : (
                  <p></p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RVGSidebar;

